import React, { memo, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import DropdownButton from '../../components/DropdownButtonMC';
import ButtonComponent from '../../components/ButtonMC';
import DropdownMC from '../../components/DropdownMC';
import IconMC from '../../components/IconMC';
import useHeaderChatWindowUI from '../../hooks/inbox/ui/useHeaderChatWindowUI';
import CallButton from '../calls/CallButton';
import CustomerChannelsSelectorMC from './components/CustomerChannelsSelectorMC';

const HeaderChatWindowMC = memo(({
  onMobileClose,
  onMobileToggleRightSidebar,
  showMobileRightSidebar = false,
  isSidebar = false
}) => {
  const { t } = useTranslation('InboxMC');
  const {
    agentListOptions,
    chatStateOptions,
    selectedAgent,
    canAssignChatsToAgents,
    canToggleCustomerBlock,
    showAssignToSelfButton,
    handleAssignToSelf,
    statusChatLabel,
    fullNameCustomer,
    blockUserTooltiptext,
    handleToggleBlockUser,
    userIsBlocked,
    isUnRead,
    hasAllowFlow,
    handleUnreadChat,
    flowTooltiptext,
    handleToggleChatFlow,
    handleChangeAgent,
    showButtonBlockUser,
    showButtonFlow,
    showButtonFonemaCall,
    fonemaCallCustomer
  } = useHeaderChatWindowUI();

  const agentDropdown = (
    <div
      className="tw-flex tw-items-center tw-min-w-0"
      data-tooltip-id={!canAssignChatsToAgents ? 'tooltip-assign-permission-blocked' : undefined}
      data-tooltip-content={
        !canAssignChatsToAgents
          ? t('header.assign_permission_blocked_notice', {
            defaultValue: 'No tienes permiso para asignar chats a otros agentes'
          })
          : undefined
      }
    >
      <DropdownMC
        search
        options={agentListOptions}
        onSelect={handleChangeAgent}
        defaultValue={selectedAgent}
        placeholder={selectedAgent}
        className={`!tw-w-full tw-min-w-0 ${isSidebar ? '!tw-max-w-[180px] sm:!tw-w-[180px]' : '!tw-max-w-[230px] sm:!tw-w-[230px]'}`}
        labelClassName={`!tw-text-gray-600 tw-text-default ${isSidebar ? '!tw-max-w-[130px]' : '!tw-max-w-[180px]'}`}
        buttonClassName="!tw-h-[40px]"
        disabled={!canAssignChatsToAgents}
        renderSelected={(option) => (
          <div className="tw-flex tw-items-center tw-gap-1 tw-min-w-0 tw-w-full">
            {!canAssignChatsToAgents && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-text-gray-400 tw-flex-shrink-0">
                <IconMC name="lock-outline" size="sm" />
              </span>
            )}
            <span className={`tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap ${isSidebar ? 'tw-max-w-[120px]' : 'tw-max-w-[160px]'}`}>
              {option?.label || ''}
            </span>
          </div>
        )}
      />
    </div>
  );

  const assignToSelfButton = !canAssignChatsToAgents && showAssignToSelfButton ? (
    <ButtonComponent
      variant="SECONDARY"
      size="small"
      text={t('header.assign_to_me')}
      className="tw-h-[40px] tw-shrink-0"
      onClick={handleAssignToSelf}
    />
  ) : null;

  const actionButtons = (
    <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end tw-gap-1">
      {showButtonBlockUser && (
        <div
          data-tooltip-id="tooltip-block-user"
          data-tooltip-content={blockUserTooltiptext}
        >
          <ButtonComponent
            outlined={!userIsBlocked || !canToggleCustomerBlock}
            variant={userIsBlocked && canToggleCustomerBlock ? 'SECONDARY' : 'GRAY'}
            icon="stop-outlined"
            size="medium"
            className={`tw-h-[40px] tw-shrink-0 tw-text-default ${userIsBlocked && canToggleCustomerBlock ? '!tw-bg-blue-50 !tw-border !tw-border-blue-500' : ''}`}
            iconClassName={userIsBlocked && canToggleCustomerBlock ? 'tw-text-blue-500' : ''}
            onClick={handleToggleBlockUser}
            disabled={!canToggleCustomerBlock}
          />
        </div>
      )}
      <ButtonComponent
        outlined={!isUnRead}
        variant={isUnRead ? 'SECONDARY' : 'GRAY'}
        icon="inbox-outline"
        size="medium"
        className={`tw-h-[40px] tw-shrink-0 tw-text-default ${isUnRead ? '!tw-bg-blue-50 !tw-border !tw-border-blue-500' : ''}`}
        iconClassName={isUnRead ? 'tw-text-blue-500' : ''}
        data-tooltip-id="tooltip-mark-unread"
        data-tooltip-content={t('header.mark_unread')}
        onClick={handleUnreadChat}
      />
      {showButtonFlow && (
        <ButtonComponent
          outlined={!hasAllowFlow}
          variant={hasAllowFlow ? 'SECONDARY' : 'GRAY'}
          icon="carbon-flow"
          size="medium"
          className={`tw-h-[40px] tw-shrink-0 tw-text-default ${hasAllowFlow ? '!tw-bg-blue-50 !tw-border !tw-border-blue-500' : ''}`}
          iconClassName={hasAllowFlow ? 'tw-text-blue-500' : ''}
          data-tooltip-id="tooltip-bot-toggle"
          data-tooltip-content={flowTooltiptext}
          onClick={handleToggleChatFlow}
        />
      )}
      {showButtonFonemaCall && fonemaCallCustomer && (
        <CallButton customer={fonemaCallCustomer} />
      )}
      {onMobileToggleRightSidebar && (
        <ButtonComponent
          outlined={!showMobileRightSidebar}
          variant={showMobileRightSidebar ? 'SECONDARY' : 'GRAY'}
          icon="person-outline"
          size="medium"
          className={`tw-h-[40px] tw-shrink-0 tw-text-default md:tw-hidden ${showMobileRightSidebar ? '!tw-bg-blue-50 !tw-border !tw-border-blue-500' : ''}`}
          iconClassName={showMobileRightSidebar ? 'tw-text-blue-500' : ''}
          data-tooltip-id="tooltip-customer-sidebar"
          data-tooltip-content={t('sidebar.title')}
          onClick={onMobileToggleRightSidebar}
        />
      )}
      {onMobileClose && (
        <ButtonComponent
          outlined
          variant="GRAY"
          icon="close-outline"
          size="medium"
          className="tw-h-[40px] tw-shrink-0 tw-text-default md:tw-hidden"
          data-tooltip-id="tooltip-close-conversation"
          data-tooltip-content={t('chatWindow.close_conversation')}
          onClick={onMobileClose}
        />
      )}
    </div>
  );

  // Modal/sidebar: columna angosta — agente bajo los iconos, estado+canales en una sola línea.
  // Inbox full: grid a 2xl con agente en la 1ª fila.
  const headerClassName = isSidebar
    ? 'tw-grid tw-min-w-0 tw-w-full tw-shrink-0 tw-grid-cols-[minmax(0,1fr)_auto] tw-gap-x-2 tw-gap-y-1 tw-px-3 tw-py-2 tw-bg-white tw-border-b tw-border-gray-200 tw-items-start'
    : 'tw-grid tw-min-w-0 tw-w-full tw-shrink-0 tw-grid-cols-[minmax(0,1fr)_auto] tw-gap-x-2 tw-gap-y-1.5 tw-px-3 tw-py-2 tw-bg-white tw-pb-2 tw-border-b tw-border-gray-200 2xl:tw-grid-cols-[minmax(0,1fr)_auto_auto] 2xl:tw-items-start';

  return (
    <Suspense fallback={<div className="loading" />}>
      <div className={headerClassName}>
        <div className="tw-col-span-1 tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5 tw-self-start">
          <span
            className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-default tw-text-gray-950 tw-leading-snug"
            title={fullNameCustomer}
          >
            {fullNameCustomer}
          </span>
          <div className="tw-flex tw-min-w-0 tw-flex-nowrap tw-items-center tw-gap-0.5">
            <span className="tw-shrink-0 tw-text-gray-950 tw-font-normal tw-text-2xs">{t('header.state')}</span>
            <DropdownButton
              variant="TEXT_PLAIN"
              text={statusChatLabel}
              items={chatStateOptions}
              className="!tw-ml-0 !tw-text-blue-500 !tw-h-[20px] !tw-min-h-0 !tw-py-0 !tw-px-1"
            />
            <CustomerChannelsSelectorMC compact={isSidebar} />
          </div>
        </div>
        {isSidebar ? (
          <div className="tw-col-span-1 tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-gap-1 tw-self-start">
            {actionButtons}
            <div className="tw-flex tw-min-w-0 tw-items-center tw-justify-end tw-gap-2">
              {agentDropdown}
              {assignToSelfButton}
            </div>
          </div>
        ) : (
          <>
            <div className="tw-col-span-1 tw-flex tw-shrink-0 tw-items-center tw-justify-end tw-self-start">
              {actionButtons}
            </div>
            <div className="tw-col-span-2 tw-flex tw-min-w-0 tw-justify-end tw-self-start tw-gap-2 2xl:tw-col-span-1">
              {agentDropdown}
              {assignToSelfButton}
            </div>
          </>
        )}
        <ReactTooltip id="tooltip-block-user" place="bottom" className="tw-z-50" />
        <ReactTooltip id="tooltip-mark-unread" place="bottom" className="tw-z-50" />
        <ReactTooltip id="tooltip-bot-toggle" place="bottom" className="tw-z-50" />
        <ReactTooltip id="tooltip-call-action" place="bottom" className="tw-z-50" />
        <ReactTooltip id="tooltip-customer-sidebar" place="bottom" className="tw-z-50" />
        <ReactTooltip id="tooltip-close-conversation" place="bottom" className="tw-z-50" />
        <ReactTooltip id="tooltip-assign-permission-blocked" place="bottom" className="tw-z-50" />
      </div>
    </Suspense>
  );
});

HeaderChatWindowMC.displayName = 'HeaderChatWindowMC';

export default HeaderChatWindowMC;
